import { API_CONFIG, API_ENDPOINTS, HTTP_METHODS, ApiResponse, RequestOptions } from './config';
import { tokenRefreshService } from '../../services/TokenRefreshService';

// Exported so callers can throw a STRUCTURED error (preserving error/details/
// code/meta/statusCode/errors) instead of the lossy `throw new Error(result.error)`,
// which discards everything the friendly-error layer needs. Self-contained
// (extends Error, no imports) so exporting introduces no circular dependency.
export class ApiError extends Error {
  error: string;
  message: string;
  details?: any;
  code?: string;
  statusCode?: number;
  meta?: any;
  errors?: Record<string, string>;

  constructor({ error, message, details, code, statusCode, meta, errors }: { error: any; message?: string; details?: any; code?: string; statusCode?: number; meta?: any; errors?: Record<string, string> }) {
    // Extract string message from potentially complex error object
    let finalMessage = message;
    if (!finalMessage) {
      if (typeof error === 'string') {
        finalMessage = error;
      } else if (error && typeof error === 'object') {
        finalMessage = error.message || error.error || JSON.stringify(error);
      } else {
        finalMessage = 'An unknown error occurred';
      }
    }

    super(finalMessage);
    this.name = 'ApiError';
    this.error = typeof error === 'string' ? error : (error?.message || finalMessage);
    this.message = finalMessage;
    this.details = details;
    this.code = code;
    this.statusCode = statusCode;
    this.meta = meta;
    this.errors = errors;
  }

  /**
   * Build a structured ApiError from a failed apiClient result
   * (`{ error, details, code, meta, statusCode, errors }`). Use at throw sites
   * instead of `throw new Error(result.error)` so `showApiError` can map by machine
   * code / surface the backend's specific `details`.
   *
   *   const res = await api.doThing(...)
   *   if (res.error) throw ApiError.fromResult(res, 'Could not do the thing')
   */
  static fromResult(
    result: { error?: any; message?: string; details?: any; code?: string; meta?: any; statusCode?: number; errors?: Record<string, string> } | null | undefined,
    fallbackMessage?: string,
  ): ApiError {
    const r = result ?? {};
    return new ApiError({
      error: r.error ?? r.message ?? fallbackMessage ?? 'Request failed',
      message: typeof r.error === 'string' ? r.error : (r.message ?? fallbackMessage),
      details: r.details,
      code: r.code,
      meta: r.meta,
      statusCode: r.statusCode,
      errors: r.errors,
    });
  }
}

class ApiClient {
  private baseUrl: string;
  private retryCount = 0;
  private maxRetries = 1;

  constructor() {
    this.baseUrl = API_CONFIG.BASE_URL;
  }

  /**
   * Check if an endpoint is public (doesn't require authentication)
   */
  private isPublicEndpoint(endpoint: string): boolean {
    const publicEndpoints = [
      '/scheduling/event-types/slug/',
      '/scheduling/event-types/org/',
      '/scheduling/event-types/', // Public for viewing event details on booking success
      '/scheduling/bookings', // Public for creating and viewing bookings
      '/scheduling/availability/',
    ];
    return publicEndpoints.some(publicPath => endpoint.includes(publicPath));
  }

  /**
   * Make an HTTP request with automatic token refresh
   */
  private async makeRequest<T = any>(
    endpoint: string,
    options: RequestOptions = {},
    isRetry: boolean = false
  ): Promise<ApiResponse<T>> {
    const {
      method = 'GET',
      headers = {},
      body,
    } = options;

    const url = `${this.baseUrl}${endpoint}`;

    // Skip authentication for public endpoints
    const isPublic = this.isPublicEndpoint(endpoint);

    // Try to ensure we have a valid token before making the request (only for protected endpoints)
    // But don't block the request if token refresh fails - let the backend handle authentication
    if (!isRetry && !isPublic) {
      await tokenRefreshService.ensureValidToken();
      // Continue with the request even if token refresh failed
      // The backend will return 401 if authentication is required
    }

    // Add authentication header if token exists
    const authHeaders: Record<string, string> = {
      ...headers,
    };

    // Only set Content-Type if there's a body and it's NOT FormData
    // FormData needs the browser to auto-set Content-Type with the multipart boundary
    const isFormData = body instanceof FormData;
    if (body && !isFormData) {
      authHeaders['Content-Type'] = 'application/json';
    }

    const token = localStorage.getItem('auth_token');
    if (token) {
      authHeaders['Authorization'] = `Bearer ${token}`;
    }

    const requestOptions: RequestInit = {
      method: HTTP_METHODS[method],
      headers: authHeaders,
      body: body ? (isFormData ? body : JSON.stringify(body)) : undefined,
    };

    try {
      const response = await fetch(url, requestOptions);

      // Handle 401 Unauthorized - token might be expired or session invalid, so refresh + retry.
      // NOTE: 403 is deliberately NOT retried. 403 means "authenticated but not allowed" — a
      // token refresh can never change that answer, so retrying is a guaranteed-useless duplicate
      // request, and if the refresh itself fails the user gets logged out on a legitimate
      // permission denial. Only 401 (bad/expired token) is fixable by refreshing.
      if (response.status === 401 && !isRetry && this.retryCount < this.maxRetries) {
        this.retryCount++;

        const refreshSuccess = await tokenRefreshService.forceRefresh();

        if (refreshSuccess) {
          // Retry the request with the new token
          this.retryCount = 0;
          return this.makeRequest<T>(endpoint, options, true);
        } else {
          // Refresh failed, clear tokens and redirect to login — UNLESS a
          // password-change logout is in progress. After changing their
          // password the current session's token is intentionally invalidated;
          // the Settings flow shows a message and signs out after a short grace
          // period. Suppress the immediate redirect during that window so the
          // grace UX isn't cut short by a background request. Self-expiring (6s)
          // so a stuck flag can never permanently disable the redirect.
          let pwChangeGrace = false;
          try {
            const ts = Number(sessionStorage.getItem('pw_change_logout_ts') || '');
            pwChangeGrace = Number.isFinite(ts) && Date.now() - ts < 6000;
          } catch {
            // sessionStorage unavailable — fall through to normal redirect.
          }
          if (!pwChangeGrace) {
            tokenRefreshService.clearTokens();
            window.location.href = '/auth';
          }
          return {
            data: null,
            error: 'Session expired - please login again',
            success: false,
          };
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // Suppress noisy 404s for optional/feature-gated endpoints (e.g. ads not yet connected)
        const isExpected404 = response.status === 404 && endpoint.startsWith('/ads/');
        if (!isExpected404) {
          console.error(`API Error [${response.status}]:`, errorData);
        }

        throw new ApiError({
          error: errorData.error || errorData.message || `HTTP ${response.status}`,
          message: errorData.message || errorData.error?.message || errorData.error || response.statusText,
          details: errorData?.details,
          statusCode: response.status,
          meta: errorData?.meta,
          // Field-level validation errors (e.g. { support_phone: "…" }) so callers
          // can show the EXACT inline error instead of a generic "Bad Request".
          errors: errorData?.errors,
        });
      }

      // Reset retry count on successful request
      this.retryCount = 0;

      const data = await response.json();

      // Handle different response formats
      if (data.success !== undefined) {
        let payload = data.data;
        if (payload === undefined) {
          if (Object.prototype.hasOwnProperty.call(data, 'template')) {
            payload = data.template;
          } else if (Object.prototype.hasOwnProperty.call(data, 'templates')) {
            payload = data.templates;
          } else if (Object.prototype.hasOwnProperty.call(data, 'account')) {
            payload = data.account;
          } else if (Object.prototype.hasOwnProperty.call(data, 'accounts')) {
            payload = data.accounts;
          } else if (Object.prototype.hasOwnProperty.call(data, 'scheduledPosts')) {
            payload = data.scheduledPosts;
          } else if ((Object.prototype.hasOwnProperty.call(data, 'tempSessionId') && Object.prototype.hasOwnProperty.call(data, 'availableAccounts')) ||
            (Object.prototype.hasOwnProperty.call(data, 'tempToken') && Object.prototype.hasOwnProperty.call(data, 'availableAccounts'))) {
            payload = data;
          }
        }
        if (payload === undefined) {
          payload = data;
        }
        return {
          data: payload,
          error: data.success ? null : (typeof data.error === 'object' ? data.error?.message : data.error),
          count: data.meta?.total ?? data.meta?.count ?? data.count,
          totalCount: data.meta?.total ?? data.totalCount ?? data.total,
          filteredTotalCount: data.filteredTotalCount,
          hasMore: data.hasMore ?? data.meta?.hasMore,
          message: data.message,
          success: data.success,
          details: data.details,
          unreadCount: data.unreadCount,
          alreadyCustomer: data.alreadyCustomer,
          pagination: data.pagination,
          flow: data.flow,
          // Some endpoints (e.g. /agency/compensations) return an aggregate
          // `stats` object alongside `data`; pass it through untyped so callers
          // can read it without a bespoke fetch.
          stats: data.stats,
          nextPageToken: data.nextPageToken ?? data.meta?.nextPageToken ?? null,
          limit: data.limit ?? data.meta?.limit,
        };
      } else {
        return {
          data: data.data || data,
          error: null,
          count: data.count,
          totalCount: data.totalCount,
          filteredTotalCount: data.filteredTotalCount,
          hasMore: data.hasMore,
          message: data.message,
          success: true,
          details: data.details,
        };
      }
    } catch (error) {
      // Improved error extraction for the catch block
      let errorMessage = 'Unknown error';
      let errorDetails = undefined;
      let errorMeta = undefined;
      // Machine-readable backend error code (e.g. "NO_AD_ACCOUNT"). `error.error` holds
      // the code from `errorData.error`, while `error.message` holds the human string.
      // Without surfacing this separately, callers that branch on a code (NotConnectedState,
      // RATE_LIMITED, etc.) only saw the message and fell through to the generic error UI.
      let errorCode: string | undefined = undefined;
      let errorFields: Record<string, string> | undefined = undefined;

      if (error instanceof ApiError) {
        errorMessage = error.message;
        errorDetails = error.details;
        errorMeta = error.meta;
        errorFields = error.errors;
        if (typeof error.error === 'string' && error.error && error.error !== error.message) {
          errorCode = error.error;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      return {
        data: null,
        error: errorMessage,
        code: errorCode,
        message: errorMessage,
        details: errorDetails,
        meta: errorMeta,
        errors: errorFields,
        success: false
      };
    }
  }


  /**
   * GET request
   */
  async get<T = any>(endpoint: string, options: Omit<RequestOptions, 'method' | 'body'> & { params?: Record<string, any> } = {}): Promise<ApiResponse<T>> {
    const { params, ...requestOptions } = options;

    let fullEndpoint = endpoint;
    if (params && Object.keys(params).length > 0) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(item => {
              queryParams.append(key, String(item));
            });
          } else {
            queryParams.append(key, String(value));
          }
        }
      });
      fullEndpoint = `${endpoint}?${queryParams.toString()}`;
    }

    return this.makeRequest<T>(fullEndpoint, { ...requestOptions, method: 'GET' });
  }

  /**
   * POST request
   */
  async post<T = any>(endpoint: string, body?: any, options: Omit<RequestOptions, 'method'> & { params?: Record<string, any> } = {}): Promise<ApiResponse<T>> {
    const { params, ...requestOptions } = options;

    let fullEndpoint = endpoint;
    if (params && Object.keys(params).length > 0) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
      fullEndpoint = `${endpoint}?${queryParams.toString()}`;
    }

    return this.makeRequest<T>(fullEndpoint, { ...requestOptions, method: 'POST', body });
  }

  /**
   * PUT request
   */
  async put<T = any>(endpoint: string, body?: any, options: Omit<RequestOptions, 'method'> = {}): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { ...options, method: 'PUT', body });
  }

  /**
   * PATCH request
   */
  async patch<T = any>(endpoint: string, body?: any, options: Omit<RequestOptions, 'method'> = {}): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { ...options, method: 'PATCH', body });
  }

  /**
   * DELETE request
   */
  async delete<T = any>(endpoint: string, options: Omit<RequestOptions, 'method'> = {}): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

// Create and export a singleton instance
export const apiClient = new ApiClient();

// Export the class for testing
export { ApiClient };
